import React from 'react'
import { Link, Navigate, useParams } from 'react-router-dom'
import { BriefcaseBusiness, Download, Landmark, LogIn, Smartphone, UserRound } from 'lucide-react'
import { appLinks } from '../../web-data'

const appIcons = {
    sales: BriefcaseBusiness,
    banker: Landmark,
    customer: UserRound,
}

const steps = [
    { icon: Download, text: 'Tap your store below' },
    { icon: Smartphone, text: 'Install the app' },
    { icon: LogIn, text: 'Sign in & get started' },
]

const getDevice = () => {
    const ua = navigator.userAgent || ''
    if (/android/i.test(ua)) return 'android'
    if (/iPad|iPhone|iPod/.test(ua) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)) return 'ios'
    return 'desktop'
}

const StoreButton = ({ href, logo, small, label, recommended }) => (
    <a
        href={href}
        target="_blank"
        rel="noreferrer"
        className={`group relative flex items-center gap-3 rounded-2xl bg-[#252c38] px-5 py-3 text-white no-underline shadow-md transition-all duration-200 hover:-translate-y-0.5 hover:bg-[#594cda] hover:text-white hover:shadow-xl ${recommended ? 'ring-4 ring-[#594cda]/30' : ''}`}
    >
        {recommended && (
            <span className="absolute -top-2.5 right-3 rounded-full bg-[#eb427e] px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-white">
                Your phone
            </span>
        )}
        <img src={logo} alt="" className="size-8 shrink-0" />
        <span className="flex flex-col text-left leading-tight">
            <span className="text-[11px] uppercase tracking-wide text-white/70">{small}</span>
            <span className="text-lg font-semibold">{label}</span>
        </span>
    </a>
)

const StoreButtons = ({ app, device }) => {
    const play = (
        <StoreButton key="play" href={app.playStore} logo="/assets/ps.webp" small="Get it on" label="Google Play" recommended={device === 'android'} />
    )
    const apple = (
        <StoreButton key="apple" href={app.appStore} logo="/assets/as.webp" small="Download on the" label="App Store" recommended={device === 'ios'} />
    )

    return (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            {device === 'ios' ? [apple, play] : [play, apple]}
        </div>
    )
}

const AppBadge = ({ appKey, size = 'lg' }) => {
    const Icon = appIcons[appKey]
    const box = size === 'lg' ? 'size-20 rounded-3xl' : 'size-14 rounded-2xl'
    const icon = size === 'lg' ? 'size-10' : 'size-7'

    return (
        <div className={`${box} mx-auto flex items-center justify-center bg-gradient-to-br from-[#594cda] to-[#eb427e] text-white shadow-lg`}>
            <Icon className={icon} strokeWidth={1.8} />
        </div>
    )
}

const SingleApp = ({ appKey, app, device }) => (
    <div className="reveal-on-scroll mx-auto max-w-xl rounded-[2rem] bg-white p-8 text-center shadow-[0_20px_60px_-20px_rgba(89,76,218,0.35)] sm:p-10">
        <AppBadge appKey={appKey} />

        <span className="mt-6 inline-block rounded-full bg-[#594cda]/10 px-4 py-1 text-sm font-semibold text-[#594cda]">
            {app.audience}
        </span>
        <h1 className="mt-4 text-3xl font-bold text-[#252c38] sm:text-4xl">MyRinBazaar {app.name}</h1>
        <p className="mx-auto mt-3 max-w-md text-lg text-[#6b747b]">{app.description}</p>

        <div className="mt-8">
            <StoreButtons app={app} device={device} />
        </div>

        <div className="mt-10 grid grid-cols-3 gap-2 border-t border-gray-100 pt-8">
            {steps.map(({ icon: Icon, text }, index) => (
                <div key={text} className="flex flex-col items-center gap-2">
                    <div className="relative flex size-11 items-center justify-center rounded-full bg-[#f1f9fc] text-[#594cda]">
                        <Icon className="size-5" />
                        <span className="absolute -right-1 -top-1 flex size-5 items-center justify-center rounded-full bg-[#eb427e] text-[11px] font-bold text-white">
                            {index + 1}
                        </span>
                    </div>
                    <p className="text-xs leading-snug text-[#6b747b] sm:text-sm">{text}</p>
                </div>
            ))}
        </div>
    </div>
)

const AllApps = ({ device }) => (
    <>
        <div className="mb-12 text-center">
            <h1 className="text-3xl font-bold text-[#252c38] sm:text-4xl">Download Our Apps</h1>
            <p className="mx-auto mt-3 max-w-xl text-lg text-[#6b747b]">Pick the app made for you. Available on Android and iPhone.</p>
        </div>

        <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
            {Object.entries(appLinks).map(([key, app]) => (
                <div key={key} className="reveal-on-scroll flex flex-col rounded-[1.75rem] bg-white p-7 text-center shadow-[0_20px_60px_-25px_rgba(89,76,218,0.35)]">
                    <AppBadge appKey={key} size="sm" />
                    <span className="mt-5 text-sm font-semibold text-[#594cda]">{app.audience}</span>
                    <Link to={`/download/${key}`} className="mt-1 text-2xl font-bold text-[#252c38] no-underline hover:text-[#594cda]">
                        {app.name}
                    </Link>
                    <p className="mb-6 mt-2 flex-1 text-[#6b747b]">{app.description}</p>
                    <div className="flex flex-col gap-3">
                        <StoreButton href={app.playStore} logo="/assets/ps.webp" small="Get it on" label="Google Play" recommended={device === 'android'} />
                        <StoreButton href={app.appStore} logo="/assets/as.webp" small="Download on the" label="App Store" recommended={device === 'ios'} />
                    </div>
                </div>
            ))}
        </div>
    </>
)

const DownloadApps = () => {
    const { app: appKey } = useParams()
    const device = getDevice()
    const app = appKey ? appLinks[appKey] : null

    if (appKey && !app) {
        return <Navigate to='/download' replace />
    }

    return (
        <section className="bg-gradient-to-b from-[#f1f9fc] via-white to-[#f1f9fc] px-4 pb-24 pt-36 sm:px-8">
            <div className="mx-auto max-w-6xl">
                {app ? <SingleApp appKey={appKey} app={app} device={device} /> : <AllApps device={device} />}
            </div>
        </section>
    )
}

export default DownloadApps
